import React from "react";
import { Box, CircularProgress, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";

type Respondent = {
  _id?: string;
  firstName?: string;
  lastName?: string;
  emailId?: string;
  surveyAnswer?: string;
  createdAt?: string;
};

type Props = {
  loading: boolean;
  surveyUserDetailsData: any;
  surveyType: "star" | "option" | "text";
};

const formatRespondentName = (r: Respondent): string => {
  const parts = [r.firstName, r.lastName].filter((p) => p && String(p).trim().length > 0);
  if (parts.length > 0) return parts.join(" ");
  if (r.emailId) return r.emailId;
  return "Unbekannt";
};

const formatScore = (answer: string | undefined, type: Props["surveyType"]): string => {
  if (!answer) return "—";
  if (type === "star") {
    const num = parseFloat(answer);
    return Number.isFinite(num) ? `${num} ⭐` : answer;
  }
  return answer;
};

const formatDate = (iso?: string): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "—";
  }
};

export const ReviewsAndRating = ({
  loading,
  surveyUserDetailsData,
  surveyType,
}: Props) => {

  const ratings: Record<string, number> = {};

  surveyUserDetailsData?.surveyAnswer?.filter((a: any) => a != null).forEach((answer: string) => {
    if (ratings[answer]) {
      ratings[answer]++;
      return;
    }

    ratings[answer] = 1;
  });

  const respondents: Respondent[] = Array.isArray(surveyUserDetailsData?.respondents)
    ? surveyUserDetailsData.respondents
    : [];
  const hasNoAnswers = Object.keys(ratings).length === 0;

  return (
    <div>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
          <CircularProgress />
        </Box>
        )
      }

      {!loading && hasNoAnswers && respondents.length === 0 && (
        <Typography
          sx={{ textAlign: "center", color: "text.secondary", mt: 3 }}
        >
          Keine Antworten vorhanden
        </Typography>
      )}

      {
        !loading && !hasNoAnswers && surveyType === "star" &&
        (
          <div className="review_box">
            <div className="col_index">
              <h4>⭐⭐⭐⭐⭐ </h4>
              <h4>⭐⭐⭐⭐ </h4>
              <h4>⭐⭐⭐ </h4>
              <h4>⭐⭐ </h4>
              <h4>⭐ </h4>
            </div>
            <div className="col_content">
              <h4>{ratings["5.0"] || "0"} Ratings</h4>
              <h4>{ratings["4.0"] || "0"} Ratings</h4>
              <h4>{ratings["3.0"] || "0"} Ratings</h4>
              <h4>{ratings["2.0"] || "0"} Ratings</h4>
              <h4>{ratings["1.0"] || "0"} Ratings</h4>
            </div>
          </div>
        )
      }

      {
        !loading && !hasNoAnswers && (surveyType === 'option' || surveyType === 'text') &&
        (
          <div>
            {
              Object.keys(ratings).map((key: string, index: number) => {
                return (
                  <div className="ratings_box" key={"ratings_box"+index}>
                    <Box sx={{ display: "flex", alignItems: "center" }} key={"center_box"+index}>
                      <h4 className="col_index " key={"col_index"+index}>{index + 1}</h4>
                      <h6 className="col_content " key={"col_content"+index}>{key}</h6>
                    </Box>
                    {
                      surveyType === 'option'
                      && <h6 className="col_count" key={"col_count"+index}> {ratings[key]} times</h6>
                    }
                  </div>
                );
              })
            }
          </div>
        )
      }

      {!loading && respondents.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Teilnehmende ({respondents.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>{surveyType === "star" ? "Bewertung" : "Antwort"}</TableCell>
                  <TableCell>Eingereicht</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {respondents.map((r) => (
                  <TableRow key={r._id ?? `${r.emailId}-${r.createdAt}`}>
                    <TableCell>{formatRespondentName(r)}</TableCell>
                    <TableCell>{formatScore(r.surveyAnswer, surveyType)}</TableCell>
                    <TableCell>{formatDate(r.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </div>
  );
};
